import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { invoiceReturnsApi, type InvoiceReturn } from "@/lib/api";
import { formatIntegratedOrderDate } from "@/lib/integratedOrders";
import {
  formatFileSize,
  invoiceReturnKindLabel,
  invoiceReturnReasonHint,
  invoiceReturnReasonLabel,
  invoiceReturnStateLabel,
} from "@/lib/invoiceReturns";

interface InvoiceReturnDetailsDialogProps {
  item: InvoiceReturn | null;
  onClose: () => void;
}

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="break-words text-sm font-medium">{value === null || value === undefined || value === "" ? "—" : value}</p>
  </div>
);

/** Chamada pendente do ERP em detalhe: o motivo, o que fazer, as tentativas e o que veio no corpo guardado. */
export function InvoiceReturnDetailsDialog({ item, onClose }: InvoiceReturnDetailsDialogProps) {
  const { data: detail, isLoading, isError } = useQuery({
    queryKey: ["invoice-returns", "detail", item?.id],
    queryFn: () => invoiceReturnsApi.getDetail(item!.id),
    enabled: item != null,
  });
  const hint = invoiceReturnReasonHint(detail?.reason);

  return (
    <Dialog open={item != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Chamada {item?.id}
            {detail && (
              <Badge variant={detail.status === "pending" ? "destructive" : "secondary"}>
                {invoiceReturnStateLabel(detail.status)}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {item ? `${invoiceReturnKindLabel(item.kind)} recebida em ${formatIntegratedOrderDate(item.receivedAt)}.` : ""}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-24" /></div>
        ) : isError || !detail ? (
          <p className="text-sm text-destructive">Não foi possível carregar a chamada.</p>
        ) : (
          <div className="space-y-5">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">{invoiceReturnReasonLabel(detail.reason)}</p>
              {detail.message && <p className="mt-1">{detail.message}</p>}
              {hint && <p className="mt-2 text-muted-foreground">O que fazer: {hint}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Field label="Pedido" value={detail.orderNumber} />
              <Field label="Pré-pedido (CNX-)" value={detail.preOrderId} />
              <Field label="Número de integração" value={detail.preOrderReference} />
              {detail.kind === "invoice" ? (
                <>
                  <Field
                    label="Nota fiscal"
                    value={detail.invoiceId
                      ? detail.invoiceId
                      : detail.derivedInvoiceId
                        ? `${detail.derivedInvoiceId} (lida do XML)`
                        : null}
                  />
                  <Field label="OV" value={detail.salesId} />
                  <Field label="Chave de acesso" value={detail.accessKey} />
                </>
              ) : (
                <>
                  <Field label="OV" value={detail.salesId} />
                  <Field label="Status" value={detail.erpStatus} />
                  <Field label="Status do pré-pedido" value={detail.preOrderStatus} />
                </>
              )}
              <Field
                label="Tentativas de reprocessamento"
                value={detail.attempts === 0
                  ? "Nenhuma"
                  : `${detail.attempts}${detail.lastAttemptAt ? ` · última em ${formatIntegratedOrderDate(detail.lastAttemptAt)}` : ""}${detail.lastAttemptBy ? ` por ${detail.lastAttemptBy}` : ""}`}
              />
              <Field
                label="Corrigida"
                value={detail.editedAt
                  ? `${formatIntegratedOrderDate(detail.editedAt)}${detail.editedBy ? ` por ${detail.editedBy}` : ""}`
                  : "Não"}
              />
            </div>

            {detail.kind === "invoice" && detail.files && detail.files.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium">Arquivos recebidos (guardados com a chamada)</p>
                <ul className="space-y-1 text-sm">
                  {detail.files.map((file, index) => (
                    <li key={`${file.type}-${index}`} className="rounded-md border px-3 py-2">
                      <span className="font-medium uppercase">{file.type ?? "?"}</span>
                      {file.fileName ? ` · ${file.fileName}` : ""} · {formatFileSize(file.sizeBytes)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.kind === "status" && detail.orders && detail.orders.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium">OVs recebidas</p>
                <ul className="space-y-1 text-sm">
                  {detail.orders.map((order, index) => (
                    <li key={`${order.salesId}-${index}`} className="rounded-md border px-3 py-2">
                      <span className="font-medium">OV {order.salesId ?? "—"}</span> · status {order.salesStatus ?? "—"}
                      {order.fiscalDocumentNumber ? ` · NF ${order.fiscalDocumentNumber}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!detail.payloadReadable && detail.rawPayload && (
              <div>
                <p className="mb-2 text-sm font-medium">Corpo recebido (ilegível)</p>
                <pre className="max-h-64 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">{detail.rawPayload}</pre>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
